import { ArrowRight } from 'lucide-react';

import { type OutreachLaneKey } from '@apps/web/components/admin/outreach_observability/lane_filter';
import { Badge } from '@apps/web/components/ui/badge';
import { cn } from '@apps/web/lib/utils';
import {
    type AutoOutreachPipeline,
    type AutoOutreachPipelineStage,
    type AutoOutreachPipelineTag,
} from '@libraries/typescript/api_client';

const IMPORTANT_ACTIONS = new Map([
    ['added-people', 0],
    ['monitoring-add-people-intent', 1],
    ['phoebe_attributed_fill', 2],
    ['assigned_elsewhere', 3],
]);

type MergedStage = {
    readonly key: string;
    readonly label: string;
    readonly count: number;
    readonly origins: Array<AutoOutreachPipelineTag>;
    readonly actions: Array<AutoOutreachPipelineTag>;
};

function mergeTags(
    tagLists: Array<Array<AutoOutreachPipelineTag>>,
): Array<AutoOutreachPipelineTag> {
    const merged = new Map<string, AutoOutreachPipelineTag>();
    for (const tags of tagLists) {
        for (const tag of tags) {
            const existing = merged.get(tag.key);
            merged.set(
                tag.key,
                existing
                    ? { ...existing, count: existing.count + tag.count }
                    : tag,
            );
        }
    }
    return [...merged.values()].sort(
        (left, right) =>
            right.count - left.count || left.label.localeCompare(right.label),
    );
}

// Stage segments are disjoint lane combinations, so summing every segment
// that intersects the selection counts each outreach/shift exactly once.
function mergeStage(
    stage: AutoOutreachPipelineStage,
    selectedLanes: ReadonlySet<OutreachLaneKey>,
): MergedStage {
    const segments = stage.segments.filter((segment) =>
        segment.lanes.some((lane) => selectedLanes.has(lane)),
    );
    return {
        key: stage.key,
        label: stage.label,
        count: segments.reduce((sum, segment) => sum + segment.count, 0),
        origins: mergeTags(segments.map((segment) => segment.origins)),
        actions: mergeTags(segments.map((segment) => segment.actions)),
    };
}

function visibleTags(tags: Array<AutoOutreachPipelineTag>) {
    return [...tags]
        .sort((left, right) => {
            const leftPriority = IMPORTANT_ACTIONS.get(left.key) ?? 100;
            const rightPriority = IMPORTANT_ACTIONS.get(right.key) ?? 100;
            return leftPriority - rightPriority || right.count - left.count;
        })
        .slice(0, 5);
}

function StageTags({
    label,
    tags,
}: {
    readonly label: string;
    readonly tags: Array<AutoOutreachPipelineTag>;
}) {
    if (tags.length === 0) {
        return null;
    }
    const shown = visibleTags(tags);
    return (
        <div className="space-y-1.5">
            <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
                {label}
            </div>
            <div className="flex flex-wrap gap-1">
                {shown.map((tag) => (
                    <Badge
                        key={tag.key}
                        variant="outline"
                        className={cn(
                            'bg-background h-5 max-w-full gap-1 px-1.5 text-[10px] font-normal',
                            IMPORTANT_ACTIONS.has(tag.key) &&
                                'border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200',
                        )}
                    >
                        <span className="truncate">{tag.label}</span>
                        <span className="text-muted-foreground tabular-nums">
                            {tag.count}
                        </span>
                    </Badge>
                ))}
                {shown.length < tags.length ? (
                    <span className="text-muted-foreground self-center text-[10px]">
                        +{tags.length - shown.length} more
                    </span>
                ) : null}
            </div>
        </div>
    );
}

function PipelineStageCard({ stage }: { readonly stage: MergedStage }) {
    const isInitial = stage.key === 'created' || stage.key === 'involved';
    return (
        <div
            className={cn(
                'bg-card relative min-h-44 overflow-hidden rounded-lg border p-3 shadow-xs',
                isInitial && 'border-violet-300 dark:border-violet-700',
            )}
        >
            <div
                className={cn(
                    'absolute inset-x-0 top-0 h-1',
                    isInitial && 'bg-violet-500',
                    stage.key === 'pending' && 'bg-amber-500',
                    stage.key === 'filled' && 'bg-emerald-500',
                    stage.key === 'unfilled' && 'bg-rose-500',
                    stage.key === 'cancelled' && 'bg-slate-500',
                )}
            />
            <div className="flex items-start justify-between gap-3 pt-1">
                <div className="text-sm font-medium">{stage.label}</div>
                <div className="text-2xl leading-none font-semibold tracking-tight tabular-nums">
                    {stage.count.toLocaleString()}
                </div>
            </div>
            <div className="mt-4 space-y-3">
                <StageTags label="Origin" tags={stage.origins} />
                <StageTags label="Actions observed" tags={stage.actions} />
            </div>
        </div>
    );
}

function PipelineTrack({
    label,
    description,
    stages,
    selectedLanes,
}: {
    readonly label: string;
    readonly description: string;
    readonly stages: Array<AutoOutreachPipelineStage>;
    readonly selectedLanes: ReadonlySet<OutreachLaneKey>;
}) {
    const [initial, ...outcomes] = stages.map((stage) =>
        mergeStage(stage, selectedLanes),
    );
    if (!initial) {
        return null;
    }
    return (
        <section aria-label={`${label} pipeline`}>
            <div className="mb-2 flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">{label}</h3>
                <p className="text-muted-foreground text-xs">{description}</p>
            </div>
            <div className="overflow-x-auto pb-2">
                <div className="grid min-w-[64rem] grid-cols-[minmax(180px,1fr)_36px_minmax(760px,4fr)] items-stretch">
                    <PipelineStageCard stage={initial} />
                    <div className="text-muted-foreground flex items-center justify-center">
                        <ArrowRight aria-hidden="true" className="size-4" />
                    </div>
                    <div className="relative grid grid-cols-4 gap-2 rounded-xl border border-dashed p-2 pt-6">
                        <span className="text-muted-foreground absolute top-1.5 left-3 text-[10px] font-semibold tracking-wider uppercase">
                            Current state
                        </span>
                        {outcomes.map((stage) => (
                            <PipelineStageCard key={stage.key} stage={stage} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export function OutreachLifecyclePipeline({
    pipeline,
    selectedLanes,
}: {
    readonly pipeline: AutoOutreachPipeline | null;
    readonly selectedLanes: ReadonlySet<OutreachLaneKey>;
}) {
    if (pipeline === null) {
        return (
            <div className="text-muted-foreground flex h-44 items-center justify-center rounded-lg border border-dashed text-sm">
                Loading lifecycle pipeline…
            </div>
        );
    }
    return (
        <div className="space-y-5">
            <PipelineTrack
                label="Outreaches"
                description="Each outreach appears once."
                stages={pipeline.outreach_stages}
                selectedLanes={selectedLanes}
            />
            <PipelineTrack
                label="Shifts"
                description="Unique shifts, classified from their latest coverage state."
                stages={pipeline.shift_stages}
                selectedLanes={selectedLanes}
            />
        </div>
    );
}
